import type Pusher from 'pusher-js';
import type { Channel } from 'pusher-js';
import { createPusher, isPusherConfigured, PusherConfigurationError } from '@/libs/pusher-client';
import type { ErrorCode } from '@fluenceur/types';
import { pusherService } from './pusher-service';

const DEBUG = import.meta.env.DEV;

/** Subscription timeout in milliseconds */
const SUBSCRIPTION_TIMEOUT_MS = 10000;
/** Maximum number of retry attempts */
const MAX_RETRY_ATTEMPTS = 3;
/** Base delay for exponential backoff in milliseconds */
const RETRY_BASE_DELAY_MS = 1000;

const log = DEBUG ? console.log.bind(console, '[BulkInvitePusher]') : () => {};
const error = DEBUG ? console.error.bind(console, '[BulkInvitePusher]') : () => {};

/** Helper to delay execution */
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface BulkInviteProgressData {
  batchId: string;
  sent: number;
  total: number;
  failed: number;
}

interface BulkInviteCompleteData {
  batchId: string;
  sent: number;
  failed: number;
  failedEmails: string[];
}

/** Per-recipient email send outcome, emitted by the outreach send job AFTER the
 * batch has queued every item (email mode). Keyed by invitation id.
 *
 * `errorCode` is what the recap translates; it rides the same
 * `InvitationOutreachFailedPayload` the backend publishes, so dropping it here
 * would leave the recap with nothing but the English `errorMessage`. */
interface OutreachOutcomeData {
  invitationId: string;
  errorMessage?: string | null;
  errorCode?: ErrorCode | null;
}

interface BulkInviteCallbacks {
  onProgress: (data: BulkInviteProgressData) => void;
  onComplete: (data: BulkInviteCompleteData) => void;
  /** A recipient's email actually left the mailbox. */
  onOutreachSent?: (data: OutreachOutcomeData) => void;
  /** A recipient's email failed to send. */
  onOutreachFailed?: (data: OutreachOutcomeData) => void;
  onOutreachUnknown?: (data: OutreachOutcomeData) => void;
}

/**
 * Service for managing Pusher subscriptions for bulk invite progress.
 * Uses imperative subscription to ensure connection is established
 * BEFORE the API call returns.
 */
class BulkInvitePusherService {
  private pusher: Pusher | null = null;
  private channel: Channel | null = null;
  private currentChannelName: string | null = null;
  private callbacks: BulkInviteCallbacks | null = null;

  /**
   * Initialize Pusher instance (lazy initialization)
   */
  private initPusher(): Pusher {
    if (!this.pusher) {
      log('Initializing Pusher instance');
      this.pusher = createPusher({
        forceTLS: true,
        enabledTransports: ['ws', 'wss'],
        channelAuthorization: {
          endpoint: '/pusher/auth',
          transport: 'ajax',
          params: {},
          headers: {},
          customHandler: async (params, callback) => {
            try {
              const auth = await pusherService.authenticate(params.socketId, params.channelName);
              callback(null, auth);
            } catch (err) {
              error('Authentication failed:', err);
              callback(err as Error, null);
            }
          },
        },
      });
    }
    return this.pusher;
  }

  /**
   * Subscribe to bulk invite events for a brand.
   * Returns a promise that resolves when subscription is established.
   * Includes retry logic with exponential backoff.
   */
  async subscribe(brandId: string, callbacks: BulkInviteCallbacks): Promise<void> {
    if (!isPusherConfigured) {
      throw new PusherConfigurationError();
    }

    const channelName = `private-brand-${brandId}`;

    // If already subscribed to this channel, just update callbacks
    if (this.channel && this.currentChannelName === channelName) {
      log('Already subscribed to channel, updating callbacks');
      this.callbacks = callbacks;
      return;
    }

    // Unsubscribe from previous channel if different
    if (this.channel && this.currentChannelName !== channelName) {
      this.unsubscribe();
    }

    this.callbacks = callbacks;
    this.currentChannelName = channelName;

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        await this.attemptSubscription(channelName);
        return; // Success
      } catch (err) {
        lastError = err instanceof Error ? err : new Error('Unknown subscription error');
        error(`Subscription attempt ${attempt}/${MAX_RETRY_ATTEMPTS} failed:`, lastError.message);

        if (attempt < MAX_RETRY_ATTEMPTS) {
          const delayMs = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
          log(`Retrying in ${delayMs}ms...`);
          await delay(delayMs);
        }
      }
    }

    // All retries failed
    throw lastError || new Error('Pusher subscription failed after all retries');
  }

  /**
   * Attempt a single subscription to the channel
   */
  private attemptSubscription(channelName: string): Promise<void> {
    const pusher = this.initPusher();

    return new Promise((resolve, reject) => {
      log('Subscribing to channel:', channelName);
      this.channel = pusher.subscribe(channelName);

      const timeout = setTimeout(() => {
        error('Subscription timeout');
        reject(new Error('Pusher subscription timeout'));
      }, SUBSCRIPTION_TIMEOUT_MS);

      this.channel.bind('pusher:subscription_succeeded', () => {
        clearTimeout(timeout);
        log('Successfully subscribed to channel:', channelName);

        // Bind event handlers
        this.channel!.bind('bulk-invitation-progress', (data: BulkInviteProgressData) => {
          log('Received progress event:', data);
          this.callbacks?.onProgress(data);
        });

        this.channel!.bind('bulk-invitation-complete', (data: BulkInviteCompleteData) => {
          log('Received complete event:', data);
          this.callbacks?.onComplete(data);
        });

        // Per-recipient email outcomes arrive AFTER completion (the send jobs
        // run once items are queued), so the subscription must outlive the
        // complete event to flip recap lines from "sending" to "sent"/"failed".
        this.channel!.bind('invitation-outreach-complete', (data: OutreachOutcomeData) => {
          log('Received outreach-complete event:', data);
          this.callbacks?.onOutreachSent?.(data);
        });

        this.channel!.bind('invitation-outreach-failed', (data: OutreachOutcomeData) => {
          log('Received outreach-failed event:', data);
          this.callbacks?.onOutreachFailed?.(data);
        });

        this.channel!.bind('invitation-outreach-unknown', (data: OutreachOutcomeData) => {
          this.callbacks?.onOutreachUnknown?.(data);
        });

        resolve();
      });

      this.channel.bind('pusher:subscription_error', (err: Error) => {
        clearTimeout(timeout);
        error('Subscription error:', err);
        reject(err);
      });
    });
  }

  /**
   * Unsubscribe from bulk invite events
   */
  unsubscribe(): void {
    if (this.channel && this.currentChannelName) {
      log('Unsubscribing from channel:', this.currentChannelName);
      this.channel.unbind_all();
      this.pusher?.unsubscribe(this.currentChannelName);
      this.channel = null;
      this.currentChannelName = null;
      this.callbacks = null;
    }
  }

  /**
   * Disconnect Pusher entirely
   */
  disconnect(): void {
    this.unsubscribe();
    if (this.pusher) {
      log('Disconnecting Pusher');
      this.pusher.disconnect();
      this.pusher = null;
    }
  }
}

// Singleton instance
export const bulkInvitePusherService = new BulkInvitePusherService();
